// src/hooks/useDmCandidates.js
//
// Returns the list of users the current user can start a DM with.
//
// "Can DM" rule: anyone who shares at least one team_announcement
// channel with you. We piggyback on the announcement channels'
// participantIds + members map (already built in Phase 1) instead
// of running a separate roster lookup. That gives us the right
// audience for free:
//   - Coaches you co-manage teams with
//   - Parents whose kids are on your team
//   - Other parents on the same team (matches TeamSnap behavior)
//
// Excludes the current user (you can't DM yourself).
// Excludes anyone you already have an active DM with by listing
// them with the existing chatId so the picker routes to the
// existing thread instead of creating a duplicate.

import { useEffect, useMemo, useState } from 'react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export default function useDmCandidates(uid) {
  const [teamChats, setTeamChats] = useState([])
  const [dmChats, setDmChats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Subscribe to ALL chats I'm in, then split client-side. One
  // subscription is cheaper than two on the same collection.
  useEffect(() => {
    if (!uid) {
      setTeamChats([])
      setDmChats([])
      setLoading(false)
      return
    }
    setLoading(true)

    const q = query(
      collection(db, 'chats'),
      where('participantIds', 'array-contains', uid),
    )
    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        setTeamChats(all.filter((c) => c.type === 'team_announcement'))
        setDmChats(all.filter((c) => c.type === 'dm'))
        setLoading(false)
      },
      (err) => {
        console.warn('[useDmCandidates] snapshot error:', err?.code, err?.message)
        setError(err?.message || 'failed')
        setLoading(false)
      },
    )
    return () => unsub()
  }, [uid])

  // Map of other-uid -> existing DM chat id, so the picker can
  // route to the existing chat instead of creating a duplicate.
  const existingDmByUid = useMemo(() => {
    const map = {}
    dmChats.forEach((c) => {
      const other = (c.participantIds || []).find((p) => p !== uid)
      if (other) map[other] = c.id
    })
    return map
  }, [dmChats, uid])

  // Collapse all teammates from all my team channels into a unique
  // list, with displayName/photoURL pulled from the first channel
  // that has them. Skip myself.
  const candidates = useMemo(() => {
    if (!uid) return []
    const byUid = new Map()
    teamChats.forEach((chat) => {
      const members = chat.members || {}
      const ids = chat.participantIds || []
      ids.forEach((memberUid) => {
        if (memberUid === uid) return
        if (byUid.has(memberUid)) {
          // Already added; just track the additional shared team
          byUid.get(memberUid).sharedTeamIds.push(chat.teamId || chat.id)
          return
        }
        const m = members[memberUid] || {}
        byUid.set(memberUid, {
          uid: memberUid,
          displayName: m.displayName || 'Member',
          photoURL: m.photoURL || null,
          // Role is per-channel; we surface the highest privilege
          // they hold across any shared channel (admin > member)
          // so coaches show as "Coach" in the picker.
          isCoach: m.role === 'admin',
          sharedTeamIds: [chat.teamId || chat.id],
          existingDmId: null, // filled in below
        })
      })
    })
    // Annotate with existing DM if any
    byUid.forEach((entry, otherUid) => {
      entry.existingDmId = existingDmByUid[otherUid] || null
    })
    // Sort: coaches first, then alphabetical by display name
    return [...byUid.values()].sort((a, b) => {
      if (a.isCoach && !b.isCoach) return -1
      if (!a.isCoach && b.isCoach) return 1
      return a.displayName.localeCompare(b.displayName)
    })
  }, [teamChats, uid, existingDmByUid])

  return { candidates, loading, error }
}
